// Chemistry → Section 6: Chemical Changes (KS3, sourced from the
// Grade9Ready science PDF). Same rich-content template as the other
// section files. Authored topics from `authoredTopics.json` override
// any topic with a matching id at runtime.
import authored from './authoredTopics.json';

const AUTHORED_KEY = 'chemistry/chemical-changes';
const AUTHORED = (authored && authored[AUTHORED_KEY]) || [];
const AUTHORED_BY_ID = Object.fromEntries(AUTHORED.map((t) => [t.id, t]));

// =====================================================================
// 6.1  CHEMICAL REACTIONS
// =====================================================================
const REACTIONS_TOPIC = {
  id: 'chem-6.1',
  title: 'Chemical Reactions',
  slug: 'chemical-reactions',
  pageRef: 'Section 6 · Topic 1',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Physical vs chemical change',
        body:
          'A physical change does NOT make a new substance — examples: melting ice, dissolving salt in water, cutting wood. A chemical change (a chemical reaction) DOES make one or more new substances — examples: rusting iron, burning paper, mixing vinegar and bicarb. Chemical changes are usually harder to reverse and often release or absorb energy as heat.',
        check: {
          question: 'Which of these is a CHEMICAL change?',
          options: ['Melting ice', 'Dissolving sugar', 'Burning paper', 'Cutting wood'],
          answer: 'Burning paper',
          explainer: 'Burning makes new substances (ash, smoke, CO₂, water vapour). The other three are reversible physical changes.',
        },
      },
      {
        heading: 'Signs a reaction has happened',
        body:
          'Look out for: a colour change, a smell change, fizzing/bubbles (a gas being made), a precipitate (an insoluble solid forming in a liquid), a temperature change, or light/sound being given off. If you can spot one of these, a chemical reaction is probably happening.',
        check: {
          question: 'Which is NOT a sign of a chemical reaction?',
          options: ['Bubbles forming', 'Colour change', 'Temperature change', 'A liquid being poured'],
          answer: 'A liquid being poured',
          explainer: 'Pouring is just a physical move — no new substance.',
        },
      },
      {
        heading: 'Reactants and products',
        body:
          'In any chemical reaction, the substances you start with are reactants. The substances you end up with are products. We write the change as reactants → products. The arrow means "react to make". Atoms are NEVER created or destroyed — they are just rearranged into new combinations (this is the law of conservation of mass, covered next).',
        check: {
          question: 'In "magnesium + oxygen → magnesium oxide", magnesium oxide is the…',
          options: ['Reactant', 'Product', 'Catalyst', 'Solvent'],
          answer: 'Product',
          explainer: 'Reactants are on the left of the arrow; products are on the right.',
        },
      },
    ],
    keyPoints: [
      'Physical change = no new substance; chemical change = new substance(s)',
      'Signs: colour change, fizzing, precipitate, temperature change, smell',
      'Reactants → products',
      'Atoms are rearranged, not created or destroyed',
      'Reactions often release or absorb heat',
    ],
    workedExamples: [
      {
        problem: 'Classify each as physical or chemical: (i) ice melting, (ii) milk going sour, (iii) salt dissolving, (iv) iron rusting.',
        steps: [
          { step: 1, instruction: '(i) ice melting', working: 'Same molecules (H₂O) → physical' },
          { step: 2, instruction: '(ii) milk going sour', working: 'Bacteria make new acids → chemical' },
          { step: 3, instruction: '(iii) salt dissolving', working: 'Salt still salt; reversible by evaporating → physical' },
          { step: 4, instruction: '(iv) iron rusting', working: 'New substance (iron oxide) → chemical' },
        ],
        answer: 'Physical: ice melting, salt dissolving. Chemical: milk souring, rusting.',
        commonMistake: 'Calling dissolving a chemical change — the salt is still salt; you can recover it by evaporating the water.',
      },
    ],
    whyItWorks:
      'Spotting whether a change is chemical is the first step in any practical. Physical changes can be reversed easily; chemical ones almost never can. That\u2019s why chemists draw arrows, write equations, and obsess about how atoms rearrange.',
  },
  glossary: [
    { term: 'Chemical reaction', definition: 'A change where new substance(s) are made.' },
    { term: 'Physical change', definition: 'A change where no new substance is made (e.g. melting).' },
    { term: 'Reactant', definition: 'A starting substance in a chemical reaction.' },
    { term: 'Product', definition: 'A substance made by a chemical reaction.' },
    { term: 'Precipitate', definition: 'An insoluble solid formed when two solutions react.' },
  ],
  examQuestion: {
    prompt:
      '(a) State THREE signs that a chemical reaction has taken place. (b) Define reactant and product. (c) Is dissolving sugar in tea a chemical reaction? Justify. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Any 3 of: colour change / fizzing / precipitate / temperature change / smell / light or sound',
      'Reactant = starting substance / on the left of the arrow',
      'Product = substance made / on the right of the arrow',
      'No, dissolving is a physical change',
      'Sugar is unchanged; can be recovered by evaporation',
    ],
  },
  practice: {
    warmUp: [
      { id: 'chem-6.1.w1', type: 'text', question: 'Reactants → ___ .', answer: 'products', hint1: '8 letters', hint2: 'Made by a reaction', hint3: 'Right of the arrow' },
      { id: 'chem-6.1.w2', type: 'choice', question: 'Which is a chemical change?', options: ['Melting ice', 'Dissolving salt', 'Rusting iron', 'Cutting paper'], answer: 'Rusting iron', hint1: 'Makes a new substance', hint2: 'Iron + O₂ → iron oxide', hint3: 'Hard to reverse' },
      { id: 'chem-6.1.w3', type: 'choice', question: 'Bubbles forming usually mean…', options: ['No reaction', 'A gas is being made', 'Cooling', 'Mixing'], answer: 'A gas is being made', hint1: 'Sign of reaction', hint2: 'Effervescence', hint3: 'CO₂ often' },
    ],
    level1: [
      { id: 'chem-6.1.1', type: 'choice', question: 'Which is NOT a sign of a chemical reaction?', options: ['Colour change', 'Liquid being poured', 'Temperature change', 'Precipitate'], answer: 'Liquid being poured', hint1: 'Just movement', hint2: 'No new substance', hint3: 'Physical action' },
      { id: 'chem-6.1.2', type: 'text', question: 'A solid that forms when two solutions react is called a ___ .', answer: 'precipitate', hint1: '11 letters', hint2: 'Insoluble', hint3: 'Starts with p' },
    ],
    level2: [
      { id: 'chem-6.1.3', type: 'choice', question: 'In "Mg + O₂ → MgO", magnesium is a…', options: ['Reactant', 'Product', 'Catalyst', 'Solvent'], answer: 'Reactant', hint1: 'Left of arrow', hint2: 'Starting material', hint3: 'Used up' },
      { id: 'chem-6.1.4', type: 'choice', question: 'Burning candle wax — is this physical or chemical?', options: ['Physical only', 'Chemical only', 'Both', 'Neither'], answer: 'Both', hint1: 'Wax melts AND burns', hint2: 'Two changes', hint3: 'Liquid wax then combustion' },
    ],
    level3: [
      { id: 'chem-6.1.5', type: 'choice', question: 'Why is dissolving usually NOT a chemical change?', options: ['It looks the same', 'No new substance is made', 'It is fast', 'It is cold'], answer: 'No new substance is made', hint1: 'Salt is still salt', hint2: 'Reversible by evaporation', hint3: 'No new molecules' },
      { id: 'chem-6.1.6', type: 'text', question: 'Atoms are never created or destroyed — they are just ___ .', answer: 'rearranged', hint1: '10 letters', hint2: 'Form new combos', hint3: 'Conservation of mass' },
    ],
  },
};

// =====================================================================
// 6.2  WORD AND SYMBOL EQUATIONS
// =====================================================================
const EQUATIONS_TOPIC = {
  id: 'chem-6.2',
  title: 'Word and Symbol Equations',
  slug: 'word-and-symbol-equations',
  pageRef: 'Section 6 · Topic 2',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Word equations',
        body:
          'A word equation uses NAMES of substances. Reactants on the left, an arrow, products on the right. Example: hydrogen + oxygen → water. Use a "+" between substances on the same side, and an "→" arrow between sides. Word equations are quick to write and easy to read.',
        check: {
          question: 'In a word equation, what does the arrow mean?',
          options: ['"Equals"', '"Is the same as"', '"React to make"', '"Is mixed with"'],
          answer: '"React to make"',
          explainer: 'The arrow shows direction of change — left becomes right.',
        },
      },
      {
        heading: 'Symbol equations',
        body:
          'A symbol equation uses element symbols and chemical formulae. The same reaction in symbols: 2H₂ + O₂ → 2H₂O. Numbers in front (the BIG numbers) are called "balancing numbers" — they tell you how many MOLECULES of each. Numbers below (subscripts) tell you how many ATOMS in each molecule.',
        check: {
          question: 'In 2H₂O the "2" in front means…',
          options: ['Two atoms total', 'Two molecules of water', 'Two oxygens', 'Two hydrogens'],
          answer: 'Two molecules of water',
          explainer: 'Big number in front = number of molecules. Subscript 2 means 2 H atoms IN each H₂O.',
        },
      },
      {
        heading: 'Balancing equations',
        body:
          'Atoms cannot be made or destroyed (law of conservation of mass), so the same atoms must be on each side of an equation. To balance, change ONLY the big balancing numbers (never the subscripts). Example: H₂ + O₂ → H₂O is NOT balanced (2 O on the left, 1 on the right). Try 2H₂ + O₂ → 2H₂O — now we have 4 H and 2 O on each side. ✓',
        check: {
          question: 'Which numbers can you change to balance an equation?',
          options: ['Subscripts only', 'Big balancing numbers only', 'Either', 'Neither'],
          answer: 'Big balancing numbers only',
          explainer: 'Changing subscripts changes the chemical itself. Only adjust the big numbers in front.',
        },
      },
    ],
    keyPoints: [
      'Word equation: names + "+" + "→"',
      'Symbol equation: formulae with balancing numbers',
      'Big number = number of molecules',
      'Subscript = atoms in one molecule',
      'Balanced = same atoms on both sides',
      'NEVER change subscripts when balancing',
    ],
    workedExamples: [
      {
        problem: 'Balance: Mg + O₂ → MgO',
        steps: [
          { step: 1, instruction: 'Count atoms', working: 'Left: 1 Mg, 2 O. Right: 1 Mg, 1 O. NOT balanced.' },
          { step: 2, instruction: 'Try MgO × 2', working: 'Mg + O₂ → 2MgO. Right now has 2 Mg, 2 O. Left still 1 Mg.' },
          { step: 3, instruction: 'Balance Mg', working: '2Mg + O₂ → 2MgO. Now 2 Mg and 2 O on each side.' },
        ],
        answer: '2Mg + O₂ → 2MgO',
        commonMistake: 'Changing the formula MgO to MgO₂ to fix oxygen — that creates a different chemical!',
      },
    ],
    whyItWorks:
      'A balanced equation is essentially a recipe. It tells a chemist exactly how much of each reactant produces how much of each product. That\u2019s gold for industry — saves money, reduces waste.',
  },
  glossary: [
    { term: 'Word equation', definition: 'A reaction written in words: reactants → products.' },
    { term: 'Symbol equation', definition: 'A reaction written using chemical formulae.' },
    { term: 'Balanced equation', definition: 'An equation with equal numbers of each atom on both sides.' },
    { term: 'Coefficient', definition: 'The big balancing number in front of a formula.' },
    { term: 'Subscript', definition: 'The small number after an element symbol (e.g. 2 in H₂O).' },
  ],
  examQuestion: {
    prompt:
      '(a) Balance: H₂ + Cl₂ → HCl. (b) Write the word equation for the reaction in (a). (c) Explain why subscripts must NOT be changed when balancing. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'H₂ + Cl₂ → 2HCl (correctly balanced)',
      'Hydrogen + chlorine → hydrogen chloride',
      'Subscripts define WHICH chemical is present',
      'Changing them creates a different substance',
      'Use big balancing numbers only',
    ],
  },
  practice: {
    warmUp: [
      { id: 'chem-6.2.w1', type: 'text', question: 'In a word equation, the arrow → means "react to ___".', answer: 'make', hint1: '4 letters', hint2: 'Form / produce', hint3: 'Starts with m' },
      { id: 'chem-6.2.w2', type: 'choice', question: 'In 3H₂O, how many water molecules?', options: ['1', '2', '3', '6'], answer: '3', hint1: 'Big number in front', hint2: 'Single digit', hint3: '3 molecules' },
      { id: 'chem-6.2.w3', type: 'number', question: 'Total H atoms in 3H₂O?', answer: '6', hint1: '3 × 2', hint2: 'Single digit', hint3: 'Six' },
    ],
    level1: [
      { id: 'chem-6.2.1', type: 'choice', question: 'When balancing equations, you can change…', options: ['Subscripts', 'Balancing numbers', 'Both', 'Neither'], answer: 'Balancing numbers', hint1: 'Only the big numbers', hint2: 'Don\u2019t edit formulae', hint3: 'In front of substance' },
      { id: 'chem-6.2.2', type: 'text', question: 'Hydrogen + ___ → water', answer: 'oxygen', hint1: '6 letters', hint2: 'In the air', hint3: 'O₂' },
    ],
    level2: [
      { id: 'chem-6.2.3', type: 'text', question: 'Balance: __Na + Cl₂ → 2NaCl. Big number on Na = ?', answer: '2', hint1: 'Single digit', hint2: 'Match RHS Na count', hint3: 'Right side has 2 Na' },
      { id: 'chem-6.2.4', type: 'choice', question: 'Why must equations be balanced?', options: ['It looks neat', 'Conservation of mass', 'Saves ink', 'Faster to read'], answer: 'Conservation of mass', hint1: 'No atom is lost', hint2: 'Universal law', hint3: 'Atoms in = atoms out' },
    ],
    level3: [
      { id: 'chem-6.2.5', type: 'choice', question: 'Balance: CH₄ + O₂ → CO₂ + H₂O. Coefficients?', options: ['1, 1, 1, 1', '1, 2, 1, 2', '2, 2, 1, 2', '1, 2, 2, 1'], answer: '1, 2, 1, 2', hint1: 'C balanced (1=1)', hint2: 'H needs 4 on right', hint3: 'Then O needs 4 on left → 2 O₂' },
      { id: 'chem-6.2.6', type: 'text', question: 'Atoms cannot be created or destroyed — this is the law of conservation of ___ .', answer: 'mass', hint1: '4 letters', hint2: 'Total weight unchanged', hint3: 'Starts with m' },
    ],
  },
};

// =====================================================================
// 6.3  CONSERVATION OF MASS
// =====================================================================
const CONSERVATION_TOPIC = {
  id: 'chem-6.3',
  title: 'Conservation of Mass',
  slug: 'conservation-of-mass',
  pageRef: 'Section 6 · Topic 3',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'The law',
        body:
          'In any chemical reaction, the total mass of the products equals the total mass of the reactants. Atoms are simply rearranged — not created or destroyed. So if 24 g of magnesium reacts with 16 g of oxygen, you must end up with 40 g of magnesium oxide.',
        check: {
          question: '8 g of element A reacts fully with 5 g of element B. What mass of product is formed?',
          options: ['3 g', '5 g', '8 g', '13 g'],
          answer: '13 g',
          explainer: 'Mass is conserved: 8 + 5 = 13 g.',
        },
      },
      {
        heading: 'When mass APPEARS to change',
        body:
          'Sometimes a reaction looks like it loses or gains mass. (1) Burning magnesium gains mass — it picks up oxygen from the air. (2) Burning a candle loses mass — CO₂ and water vapour escape. (3) Vinegar + bicarb in an open beaker loses mass — CO₂ bubbles away. In a SEALED container, mass would always stay the same.',
        check: {
          question: 'Why does burning magnesium gain mass?',
          options: ['Particles get bigger', 'It absorbs oxygen from the air', 'It absorbs nitrogen', 'It releases hydrogen'],
          answer: 'It absorbs oxygen from the air',
          explainer: 'Mg + O₂ → MgO. The oxygen joins the magnesium, raising the mass.',
        },
      },
      {
        heading: 'Working with mass — calculations',
        body:
          'Steps: (1) Read the question and underline the masses given. (2) Apply: total mass before = total mass after. (3) Solve for the missing value. Example: 6 g of carbon + ? g of oxygen → 22 g of CO₂. Answer: 22 - 6 = 16 g of oxygen needed.',
        check: {
          question: '5 g of A + ? g of B → 17 g of AB. Find B.',
          options: ['8 g', '10 g', '12 g', '17 g'],
          answer: '12 g',
          explainer: '17 − 5 = 12 g.',
        },
      },
    ],
    keyPoints: [
      'Total mass before = total mass after a reaction',
      'Atoms are rearranged, not created or destroyed',
      'Open containers can SEEM to lose/gain mass (gases enter or leave)',
      'In a sealed container, mass always stays the same',
      'Useful for solving "missing mass" calculation questions',
    ],
    workedExamples: [
      {
        problem: '4.0 g of magnesium burns to make 6.6 g of magnesium oxide. Find the mass of oxygen used.',
        steps: [
          { step: 1, instruction: 'Apply law', working: 'Mass of products = Mass of reactants' },
          { step: 2, instruction: 'Substitute', working: '6.6 = 4.0 + mass(O₂)' },
          { step: 3, instruction: 'Rearrange', working: 'Mass(O₂) = 6.6 − 4.0 = 2.6 g' },
        ],
        answer: '2.6 g of oxygen.',
        commonMistake: 'Adding instead of subtracting. Mass of MgO is bigger than Mg because oxygen has been ADDED.',
      },
    ],
    whyItWorks:
      'Conservation of mass is a universal law. It works because chemistry just rearranges atoms — and atoms have mass. If atoms can\u2019t appear or disappear, mass can\u2019t either.',
  },
  glossary: [
    { term: 'Conservation of mass', definition: 'The total mass before a reaction equals the total mass after.' },
    { term: 'Closed system', definition: 'A reaction container where no substance can enter or leave.' },
    { term: 'Open system', definition: 'A reaction container where gases can escape or enter from the air.' },
  ],
  examQuestion: {
    prompt:
      'When 12 g of carbon burns in 32 g of oxygen, 44 g of carbon dioxide is produced. (a) Show that this obeys the law of conservation of mass. (b) Explain why a candle appears to lose mass when it burns in an open container. (4 marks)',
    maxMarks: 4,
    markSchemePoints: [
      '12 + 32 = 44 g; reactants mass = product mass',
      'So conservation of mass holds',
      'Candle releases CO₂ and water vapour as gases',
      'Gases escape into the air, so the candle (solid) appears to lose mass',
    ],
  },
  practice: {
    warmUp: [
      { id: 'chem-6.3.w1', type: 'text', question: 'Mass before = mass ___ a reaction.', answer: 'after', hint1: '5 letters', hint2: 'Same mass at end', hint3: 'Starts with a' },
      { id: 'chem-6.3.w2', type: 'number', question: '6 g of A + 4 g of B → mass of AB?', answer: '10', hint1: '6 + 4', hint2: 'Two digits', hint3: 'Add them' },
      { id: 'chem-6.3.w3', type: 'choice', question: 'Why does burning magnesium gain mass?', options: ['Particles double', 'It absorbs oxygen from the air', 'It releases hydrogen', 'It cools'], answer: 'It absorbs oxygen from the air', hint1: 'Mg + O₂ → MgO', hint2: 'Oxygen joins on', hint3: 'Open container' },
    ],
    level1: [
      { id: 'chem-6.3.1', type: 'number', question: '7 g + ? = 25 g. Find missing value.', answer: '18', hint1: '25 - 7', hint2: 'Two digits', hint3: 'Eighteen' },
      { id: 'chem-6.3.2', type: 'choice', question: 'A reaction is done in a SEALED flask. Mass of contents…', options: ['Increases', 'Decreases', 'Stays exactly the same', 'Doubles'], answer: 'Stays exactly the same', hint1: 'No gas can leave', hint2: 'Closed system', hint3: 'Conservation' },
    ],
    level2: [
      { id: 'chem-6.3.3', type: 'choice', question: 'A candle in an open dish loses mass. Why?', options: ['Oxygen leaves it', 'CO₂ and water vapour escape into the air', 'Wax becomes invisible', 'Heat takes mass'], answer: 'CO₂ and water vapour escape into the air', hint1: 'Gas products leave', hint2: 'Open container', hint3: 'Hidden mass loss' },
      { id: 'chem-6.3.4', type: 'number', question: '5.0 g Mg burns to give 8.3 g MgO. Mass of O reacted?', answer: '3.3', hint1: '8.3 - 5.0', hint2: 'One decimal', hint3: 'About three' },
    ],
    level3: [
      { id: 'chem-6.3.5', type: 'choice', question: 'Acid + carbonate fizzes in an open beaker. Mass:', options: ['Increases', 'Decreases', 'Stays the same', 'Doubles'], answer: 'Decreases', hint1: 'Gas escapes', hint2: 'CO₂ leaves', hint3: 'Open container' },
      { id: 'chem-6.3.6', type: 'text', question: 'A container where no substance can enter or leave is a ___ system.', answer: 'closed', hint1: '6 letters', hint2: 'Sealed', hint3: 'Starts with c' },
    ],
  },
};

// =====================================================================
// 6.4  ACIDS AND ALKALIS
// =====================================================================
const ACIDS_TOPIC = {
  id: 'chem-6.4',
  title: 'Acids and Alkalis',
  slug: 'acids-and-alkalis',
  pageRef: 'Section 6 · Topic 4',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'The pH scale',
        body:
          'pH is a number from 0 to 14 that tells you how acidic or alkaline a solution is. pH below 7 = acid (lower = stronger). pH above 7 = alkali (higher = stronger). pH 7 = neutral (e.g. pure water). Common acids: lemon juice (pH 2), stomach acid (pH 1–2), rainwater (pH 5–6). Common alkalis: bleach (pH 12–13), soap (pH 9–10), baking soda solution (pH 8–9).',
        check: {
          question: 'Pure water has a pH of…',
          options: ['1', '4', '7', '10'],
          answer: '7',
          explainer: 'pH 7 is neutral.',
        },
      },
      {
        heading: 'Indicators',
        body:
          'An indicator is a chemical that changes colour in acid or alkali. Litmus turns red in acid, blue in alkali. Universal indicator gives a rainbow scale: red (very acidic) → orange → yellow → green (neutral) → blue → purple (very alkaline). Phenolphthalein is colourless in acid/neutral, pink in alkali. Indicators are how we can SEE pH.',
        check: {
          question: 'Litmus turns ___ in acid and ___ in alkali.',
          options: ['Blue, red', 'Red, blue', 'Green, purple', 'Yellow, orange'],
          answer: 'Red, blue',
          explainer: '"Red In acid" — handy mnemonic.',
        },
      },
      {
        heading: 'Strong vs concentrated',
        body:
          'Watch the language. Strong/weak refers to how much an acid splits up (ionises) in water — strong acids (like HCl) ionise completely; weak acids (like vinegar) only partly. Concentrated/dilute refers to how much acid is in a given volume of water. So you can have a dilute STRONG acid, or a concentrated WEAK acid. They mean different things.',
        check: {
          question: '"Concentrated" describes…',
          options: ['How fully an acid ionises', 'How much acid is in a volume of water', 'The colour of an acid', 'The smell of an acid'],
          answer: 'How much acid is in a volume of water',
          explainer: 'Strong/weak vs concentrated/dilute is a classic exam trap — keep them separate!',
        },
      },
    ],
    keyPoints: [
      'pH scale: 0–14',
      '<7 = acid; =7 = neutral; >7 = alkali',
      'Litmus: red in acid, blue in alkali',
      'Universal indicator: rainbow → exact pH',
      'Strong vs weak ≠ concentrated vs dilute',
    ],
    workedExamples: [
      {
        problem: 'Classify each by pH: (i) pH 1 lemon juice, (ii) pH 7 water, (iii) pH 11 bleach, (iv) pH 5 black coffee.',
        steps: [
          { step: 1, instruction: '(i) pH 1', working: 'Strong acid' },
          { step: 2, instruction: '(ii) pH 7', working: 'Neutral' },
          { step: 3, instruction: '(iii) pH 11', working: 'Strong alkali' },
          { step: 4, instruction: '(iv) pH 5', working: 'Weak acid' },
        ],
        answer: '(i) strong acid, (ii) neutral, (iii) strong alkali, (iv) weak acid.',
        commonMistake: 'Mixing up strong vs concentrated — bleach (concentrated AND strong alkali) and ammonia in water (dilute weak alkali) are very different beasts.',
      },
    ],
    whyItWorks:
      'pH is one of the most useful chemical numbers — your stomach has pH 1–2 to digest food; your blood is held at pH 7.4 (any drift kills cells); soils need pH 6–7 for crops to grow well.',
  },
  glossary: [
    { term: 'pH', definition: 'A number 0–14 measuring how acidic or alkaline a solution is.' },
    { term: 'Acid', definition: 'A solution with pH < 7.' },
    { term: 'Alkali', definition: 'A solution with pH > 7 (a soluble base).' },
    { term: 'Neutral', definition: 'pH 7 — neither acidic nor alkaline.' },
    { term: 'Indicator', definition: 'A chemical that changes colour to show pH.' },
    { term: 'Litmus', definition: 'A simple indicator: red in acid, blue in alkali.' },
    { term: 'Universal indicator', definition: 'A rainbow indicator that shows the full pH scale.' },
  ],
  examQuestion: {
    prompt:
      '(a) Define acid and alkali in terms of pH. (b) Name TWO indicators and the colours each gives in acid AND alkali. (c) State the difference between "strong" and "concentrated" acids. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Acid = pH below 7; alkali = pH above 7',
      'Litmus: red in acid, blue in alkali',
      'Universal indicator: red/orange in acid, green at 7, blue/purple in alkali (any colour pair)',
      'Strong = ionises fully',
      'Concentrated = lots of acid in given volume of water (different idea)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'chem-6.4.w1', type: 'number', question: 'pH of pure water?', answer: '7', hint1: 'Neutral', hint2: 'Single digit', hint3: 'Middle of the scale' },
      { id: 'chem-6.4.w2', type: 'choice', question: 'Litmus in acid is…', options: ['Blue', 'Red', 'Green', 'Yellow'], answer: 'Red', hint1: 'Red in acid', hint2: '3 letters', hint3: 'Stop sign colour' },
      { id: 'chem-6.4.w3', type: 'choice', question: 'pH 13 is…', options: ['Strong acid', 'Weak acid', 'Neutral', 'Strong alkali'], answer: 'Strong alkali', hint1: 'High pH', hint2: 'Above 7', hint3: 'Bleach-like' },
    ],
    level1: [
      { id: 'chem-6.4.1', type: 'choice', question: 'Vinegar has a pH of about…', options: ['2', '7', '10', '14'], answer: '2', hint1: 'Weak acid', hint2: 'Single digit', hint3: 'Below 7' },
      { id: 'chem-6.4.2', type: 'text', question: 'A chemical that changes colour to show pH is an ___ .', answer: 'indicator', hint1: '9 letters', hint2: 'Like litmus', hint3: 'Starts with i' },
    ],
    level2: [
      { id: 'chem-6.4.3', type: 'choice', question: 'A "concentrated" weak acid means…', options: ['It ionises fully', 'There is a lot of acid in the water but only some ionises', 'It is dilute', 'It tastes strong'], answer: 'There is a lot of acid in the water but only some ionises', hint1: 'Concentrated = amount', hint2: 'Weak = ionisation', hint3: 'Two separate ideas' },
      { id: 'chem-6.4.4', type: 'choice', question: 'Soap has a pH of about 9. So soap is…', options: ['Acid', 'Neutral', 'Weak alkali', 'Strong alkali'], answer: 'Weak alkali', hint1: 'Slightly above 7', hint2: 'Not very far above 7', hint3: 'pH 8–10' },
    ],
    level3: [
      { id: 'chem-6.4.5', type: 'choice', question: 'Universal indicator goes red in…', options: ['Strong acid', 'Strong alkali', 'Neutral', 'Concentrated salt'], answer: 'Strong acid', hint1: 'Low end of rainbow', hint2: 'pH 0–2', hint3: 'Very acidic' },
      { id: 'chem-6.4.6', type: 'text', question: 'Stomach acid has a pH of about ___ .', answer: '2', hint1: 'Single digit', hint2: 'Strong acid', hint3: 'Below 3' },
    ],
  },
};

// =====================================================================
// 6.5  NEUTRALISATION
// =====================================================================
const NEUTRALISATION_TOPIC = {
  id: 'chem-6.5',
  title: 'Neutralisation',
  slug: 'neutralisation',
  pageRef: 'Section 6 · Topic 5',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Acid + alkali → salt + water',
        body:
          'When you mix an acid with an alkali in the right amounts, they cancel each other out — the pH goes to about 7. This is called neutralisation. Word equation: acid + alkali → salt + water. The salt that\u2019s made depends on which acid and alkali you started with.',
        check: {
          question: 'Acid + alkali → ___ + water.',
          options: ['Gas', 'Salt', 'Acid', 'Sugar'],
          answer: 'Salt',
          explainer: 'Neutralisation always makes a salt + water. The salt name depends on which acid you used.',
        },
      },
      {
        heading: 'Naming the salt',
        body:
          'Hydrochloric acid (HCl) makes chloride salts. Sulfuric acid (H₂SO₄) makes sulfate salts. Nitric acid (HNO₃) makes nitrate salts. Examples: HCl + NaOH → NaCl (sodium chloride) + water. H₂SO₄ + 2KOH → K₂SO₄ (potassium sulfate) + 2H₂O. The metal in the salt comes from the alkali.',
        check: {
          question: 'Hydrochloric acid + sodium hydroxide makes the salt…',
          options: ['Sodium sulfate', 'Sodium nitrate', 'Sodium chloride', 'Sodium carbonate'],
          answer: 'Sodium chloride',
          explainer: 'HCl gives chlorides; sodium comes from NaOH → sodium chloride (table salt).',
        },
      },
      {
        heading: 'Everyday neutralisation',
        body:
          'Indigestion tablets are mild alkalis (e.g. magnesium hydroxide) — they neutralise excess stomach acid. Bee stings are acidic; treat with a mild alkali (toothpaste). Wasp stings are alkaline; treat with a mild acid (vinegar). Farmers add lime (calcium hydroxide) to acidic soils to bring them back to pH 6–7 for healthy crop growth.',
        check: {
          question: 'A bee sting is acidic. How would you treat it?',
          options: ['Lemon juice', 'Vinegar', 'Toothpaste (mild alkali)', 'Salt water'],
          answer: 'Toothpaste (mild alkali)',
          explainer: 'Acid + alkali = neutralisation, easing the pain.',
        },
      },
    ],
    keyPoints: [
      'Acid + alkali → salt + water',
      'HCl → chloride salts; H₂SO₄ → sulfate; HNO₃ → nitrate',
      'Metal in salt comes from the alkali',
      'Indigestion: alkali tablets neutralise stomach acid',
      'Acidic soils: add lime (calcium hydroxide)',
    ],
    workedExamples: [
      {
        problem: 'Sulfuric acid + potassium hydroxide → ?',
        steps: [
          { step: 1, instruction: 'Identify acid', working: 'Sulfuric acid → sulfate salts' },
          { step: 2, instruction: 'Identify metal', working: 'Potassium (from KOH)' },
          { step: 3, instruction: 'Salt name', working: 'Potassium sulfate' },
        ],
        answer: 'Potassium sulfate + water.',
        commonMistake: 'Forgetting water as a product — neutralisation always makes water too.',
      },
    ],
    whyItWorks:
      'Neutralisation works because acids release H⁺ ions and alkalis release OH⁻ ions. Together they form H₂O — neutral water — and leave the leftover metal + non-metal ions to make a salt.',
  },
  glossary: [
    { term: 'Neutralisation', definition: 'A reaction between an acid and an alkali that produces a salt and water.' },
    { term: 'Salt', definition: 'An ionic compound formed from the metal of the alkali and the non-metal part of the acid.' },
    { term: 'Indigestion remedy', definition: 'A mild alkali (e.g. magnesium hydroxide) used to neutralise excess stomach acid.' },
    { term: 'Lime', definition: 'Calcium hydroxide — used to neutralise acidic soils.' },
  ],
  examQuestion: {
    prompt:
      '(a) Write the general word equation for neutralisation. (b) Name the salt formed from nitric acid + sodium hydroxide. (c) Explain why farmers add lime to acidic soils. (4 marks)',
    maxMarks: 4,
    markSchemePoints: [
      'Acid + alkali → salt + water',
      'Sodium nitrate (and water)',
      'Lime (calcium hydroxide) is an alkali',
      'It neutralises the acid in the soil so crops can grow well (pH ~6–7)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'chem-6.5.w1', type: 'text', question: 'Acid + alkali → salt + ___ .', answer: 'water', hint1: '5 letters', hint2: 'H₂O', hint3: 'Liquid by-product' },
      { id: 'chem-6.5.w2', type: 'choice', question: 'A wasp sting is alkaline. Best treatment?', options: ['Soap', 'Vinegar', 'Bleach', 'Sugar water'], answer: 'Vinegar', hint1: 'Need an acid', hint2: 'Mild acid', hint3: 'Found in kitchens' },
      { id: 'chem-6.5.w3', type: 'choice', question: 'Hydrochloric acid makes ___ salts.', options: ['Sulfate', 'Nitrate', 'Chloride', 'Carbonate'], answer: 'Chloride', hint1: 'Cl', hint2: '8 letters', hint3: 'NaCl is one' },
    ],
    level1: [
      { id: 'chem-6.5.1', type: 'choice', question: 'Sulfuric acid + ammonia makes…', options: ['Ammonium chloride', 'Ammonium sulfate', 'Sodium sulfate', 'Calcium nitrate'], answer: 'Ammonium sulfate', hint1: 'Sulfuric → sulfate', hint2: 'Metal from alkali (ammonium)', hint3: 'Used as fertiliser' },
      { id: 'chem-6.5.2', type: 'text', question: 'Indigestion tablets are mild ___ that cancel stomach acid.', answer: 'alkalis', hint1: '7 letters', hint2: 'Above pH 7', hint3: 'Plural' },
    ],
    level2: [
      { id: 'chem-6.5.3', type: 'choice', question: 'A neutralised solution has pH about…', options: ['1', '4', '7', '12'], answer: '7', hint1: 'Neutral', hint2: 'Single digit', hint3: 'Middle of scale' },
      { id: 'chem-6.5.4', type: 'choice', question: 'Why is lime added to acidic soils?', options: ['To moisten them', 'To neutralise acid', 'To kill weeds', 'To darken colour'], answer: 'To neutralise acid', hint1: 'Lime = alkali', hint2: 'Helps crops', hint3: 'Raises pH towards 7' },
    ],
    level3: [
      { id: 'chem-6.5.5', type: 'choice', question: 'Nitric acid + potassium hydroxide → ?', options: ['Potassium chloride + water', 'Potassium nitrate + water', 'Potassium sulfate + water', 'Sodium nitrate + water'], answer: 'Potassium nitrate + water', hint1: 'Nitric → nitrate', hint2: 'K from KOH', hint3: 'Used in fireworks' },
      { id: 'chem-6.5.6', type: 'text', question: 'A salt + water is the product of acid + ___ .', answer: 'alkali', hint1: '6 letters', hint2: 'Above pH 7', hint3: 'Starts with a' },
    ],
  },
};

// =====================================================================
// 6.6  REACTIVITY SERIES
// =====================================================================
const REACTIVITY_TOPIC = {
  id: 'chem-6.6',
  title: 'Reactivity Series',
  slug: 'reactivity-series',
  pageRef: 'Section 6 · Topic 6',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'A list of metals from most to least reactive',
        body:
          'The reactivity series ranks metals by how readily they react. From most to least reactive (basic GCSE list): K, Na, Ca, Mg, Al, Zn, Fe, Cu, Ag, Au. A common mnemonic: Please Send Charlie\u2019s Monkeys And Zebras In Cages, Securely Guarded. (Potassium = very vigorous; Gold = pretty unreactive even in air.)',
        check: {
          question: 'Which is MOST reactive?',
          options: ['Iron', 'Magnesium', 'Sodium', 'Copper'],
          answer: 'Sodium',
          explainer: 'Sodium reacts vigorously with water; magnesium needs heat; iron rusts slowly; copper barely reacts.',
        },
      },
      {
        heading: 'Reactions with water and acid',
        body:
          'With cold water: K and Na react vigorously (K can ignite the hydrogen!), Ca steadily, Mg slowly. With dilute acid (HCl): K and Na too violent to use; Mg fizzes fast; Zn fizzes; Fe slowly; Cu and below — no reaction. The general patterns: acid + metal → salt + hydrogen. Water + reactive metal → metal hydroxide + hydrogen.',
        check: {
          question: 'Acid + metal → ___ + hydrogen.',
          options: ['Water', 'Salt', 'Oxygen', 'Carbon dioxide'],
          answer: 'Salt',
          explainer: 'A salt and hydrogen gas are made — fizzing is the H₂.',
        },
      },
      {
        heading: 'Why ranking matters',
        body:
          'Knowing the order lets you predict which metals tarnish, rust, or stay bright. Gold and platinum never tarnish — used in jewellery. Iron rusts (Fe + O₂ + H₂O → iron oxide). Aluminium has a useful trick: it reacts FAST with oxygen but the thin oxide layer protects the metal underneath, making aluminium look unreactive. So in real life, we coat iron in zinc (galvanising) so the more reactive zinc rusts first.',
        check: {
          question: 'Why is gold used in jewellery?',
          options: ['It is the cheapest metal', 'It is unreactive — does not tarnish', 'It conducts well', 'It is heavy'],
          answer: 'It is unreactive — does not tarnish',
          explainer: 'Gold sits at the BOTTOM of the reactivity series — barely reacts.',
        },
      },
    ],
    keyPoints: [
      'Order (most → least): K, Na, Ca, Mg, Al, Zn, Fe, Cu, Ag, Au',
      'Acid + metal → salt + hydrogen',
      'Water + reactive metal → hydroxide + hydrogen',
      'K, Na react with COLD water; Mg only steam',
      'Cu, Ag, Au stay bright (low reactivity)',
      'Galvanising: zinc protects iron because it is MORE reactive',
    ],
    workedExamples: [
      {
        problem: 'Predict which gives the FASTEST fizzing in dilute hydrochloric acid: zinc, copper, or magnesium.',
        steps: [
          { step: 1, instruction: 'Locate in series', working: 'Mg > Zn > Cu' },
          { step: 2, instruction: 'Match to fizzing speed', working: 'Higher reactivity = faster fizz' },
        ],
        answer: 'Magnesium fizzes fastest. Copper barely reacts.',
        commonMistake: 'Saying copper fizzes a little — copper does NOT react with dilute acid.',
      },
    ],
    whyItWorks:
      'The reactivity series reflects how easily an atom loses outer electrons. The looser those electrons, the more reactive the metal. Group 1 metals (top of the series) lose their single outer electron readily; gold holds on tight.',
  },
  glossary: [
    { term: 'Reactivity series', definition: 'A list of metals in order of how easily they react.' },
    { term: 'Galvanising', definition: 'Coating iron with zinc so the zinc rusts first, protecting the iron.' },
    { term: 'Tarnish', definition: 'A dull layer that forms on a metal as it reacts with air over time.' },
    { term: 'Rust', definition: 'The flaky orange iron oxide formed when iron reacts with oxygen and water.' },
  ],
  examQuestion: {
    prompt:
      'A student adds three metals (zinc, copper, magnesium) to dilute hydrochloric acid. (a) Predict the speed of fizzing for each, with reasons using the reactivity series. (b) Explain why iron is often coated in zinc rather than copper. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Mg fastest fizzing — most reactive of the three',
      'Zn moderate fizz — middle of the three',
      'Cu no reaction — below H in reactivity / unreactive with acid',
      'Zinc is MORE reactive than iron, so zinc rusts first',
      'Copper is LESS reactive — wouldn\u2019t protect the iron sacrificially',
    ],
  },
  practice: {
    warmUp: [
      { id: 'chem-6.6.w1', type: 'text', question: 'Acid + metal → salt + ___ .', answer: 'hydrogen', hint1: '8 letters', hint2: 'H₂', hint3: 'Pop test gas' },
      { id: 'chem-6.6.w2', type: 'choice', question: 'Most reactive of these?', options: ['Copper', 'Magnesium', 'Sodium', 'Iron'], answer: 'Sodium', hint1: 'Group 1 metal', hint2: 'Reacts vigorously with cold water', hint3: 'Stored under oil' },
      { id: 'chem-6.6.w3', type: 'choice', question: 'Why is gold used in jewellery?', options: ['It is heavy', 'It is unreactive', 'It conducts heat', 'It is cheap'], answer: 'It is unreactive', hint1: 'Bottom of reactivity series', hint2: 'No tarnish', hint3: 'Stays shiny' },
    ],
    level1: [
      { id: 'chem-6.6.1', type: 'text', question: 'The thin protective layer that forms on aluminium is aluminium ___ .', answer: 'oxide', hint1: '5 letters', hint2: 'Reacts with O₂', hint3: 'Starts with o' },
      { id: 'chem-6.6.2', type: 'choice', question: 'Which metal will NOT react with dilute hydrochloric acid?', options: ['Magnesium', 'Zinc', 'Iron', 'Copper'], answer: 'Copper', hint1: 'Below H in series', hint2: 'Stays still', hint3: 'No fizzing' },
    ],
    level2: [
      { id: 'chem-6.6.3', type: 'choice', question: 'Galvanising means coating iron with…', options: ['Aluminium', 'Tin', 'Zinc', 'Gold'], answer: 'Zinc', hint1: 'More reactive than iron', hint2: 'Sacrificial protection', hint3: '4 letters' },
      { id: 'chem-6.6.4', type: 'text', question: 'In the reactivity series, magnesium sits between calcium and ___ .', answer: 'aluminium', hint1: '9 letters', hint2: 'Symbol Al', hint3: 'Used in cans' },
    ],
    level3: [
      { id: 'chem-6.6.5', type: 'choice', question: 'A "more reactive" metal is one that…', options: ['Loses outer electrons easily', 'Holds onto electrons tightly', 'Has more neutrons', 'Has more protons'], answer: 'Loses outer electrons easily', hint1: 'Forming + ions', hint2: 'Linked to electron loss', hint3: 'Easy to ionise' },
      { id: 'chem-6.6.6', type: 'text', question: 'Iron + oxygen + water → ___ (the orange flaky stuff).', answer: 'rust', hint1: '4 letters', hint2: 'Iron oxide', hint3: 'Starts with r' },
    ],
  },
};

// =====================================================================
// 6.7  DISPLACEMENT REACTIONS
// =====================================================================
const DISPLACEMENT_TOPIC = {
  id: 'chem-6.7',
  title: 'Displacement Reactions',
  slug: 'displacement-reactions',
  pageRef: 'Section 6 · Topic 7',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'The big idea',
        body:
          'A more reactive metal can KICK OUT (displace) a less reactive metal from a compound. The general pattern: more-reactive metal + less-reactive metal\u2019s salt → more-reactive metal\u2019s salt + less-reactive metal. The less reactive metal is left behind as a solid.',
        check: {
          question: 'Which type of metal can displace another from its compound?',
          options: ['A less reactive one', 'A more reactive one', 'Any metal', 'No metal can'],
          answer: 'A more reactive one',
          explainer: 'You need to be HIGHER in the reactivity series to push a metal out of its compound.',
        },
      },
      {
        heading: 'Worked example: zinc + copper sulfate',
        body:
          'Add zinc metal to blue copper sulfate solution. Zinc is more reactive than copper. So: Zn + CuSO₄ → ZnSO₄ + Cu. Observations: the blue solution fades (CuSO₄ → ZnSO₄, which is colourless), and a layer of pinkish copper appears on the zinc strip. Reverse it (copper + zinc sulfate) and NOTHING happens — copper isn\u2019t reactive enough to displace zinc.',
        check: {
          question: 'In Zn + CuSO₄ → ZnSO₄ + Cu, what is displaced?',
          options: ['Sulfur', 'Oxygen', 'Copper', 'Zinc'],
          answer: 'Copper',
          explainer: 'Zinc kicks copper out of the compound; copper appears as a solid.',
        },
      },
      {
        heading: 'Practical uses',
        body:
          'Industrial extraction of less reactive metals: aluminium can displace iron from iron oxide in the thermite reaction (used to weld railway tracks). Iron is used to displace less reactive metals like silver from waste solutions. Knowing the reactivity order also helps you predict which displacement WON\u2019T happen — saves wasted lab time.',
        check: {
          question: 'Will copper displace silver from silver nitrate solution?',
          options: ['No — copper is less reactive', 'Yes — copper is more reactive than silver', 'No — they are the same', 'Yes — all metals can'],
          answer: 'Yes — copper is more reactive than silver',
          explainer: 'Copper sits ABOVE silver in the reactivity series, so copper displaces silver.',
        },
      },
    ],
    keyPoints: [
      'More reactive metal displaces less reactive from its compound',
      'Zn + CuSO₄ → ZnSO₄ + Cu (classic example)',
      'Solution colour changes; less reactive metal appears as a solid',
      'Reverse direction: NO reaction',
      'Use the reactivity series to predict outcomes',
    ],
    workedExamples: [
      {
        problem: 'Will magnesium displace zinc from zinc sulfate? Predict and write the equation.',
        steps: [
          { step: 1, instruction: 'Compare reactivity', working: 'Mg sits ABOVE Zn → more reactive' },
          { step: 2, instruction: 'So…', working: 'Mg displaces Zn' },
          { step: 3, instruction: 'Equation', working: 'Mg + ZnSO₄ → MgSO₄ + Zn' },
        ],
        answer: 'Yes, Mg + ZnSO₄ → MgSO₄ + Zn.',
        commonMistake: 'Forgetting that the reaction only goes ONE way — putting Zn into MgSO₄ does nothing.',
      },
    ],
    whyItWorks:
      'Displacement is just a tug-of-war for electrons. The more reactive metal pulls the negative bit of the salt to itself, leaving the less reactive metal as plain metal. It\u2019s a quick visible test of relative reactivity.',
  },
  glossary: [
    { term: 'Displacement reaction', definition: 'A reaction where a more reactive metal pushes a less reactive metal out of a compound.' },
    { term: 'Thermite reaction', definition: 'Aluminium displaces iron from iron oxide — used for welding railway tracks.' },
  ],
  examQuestion: {
    prompt:
      'Predict whether each happens. Explain. (a) Iron + copper sulfate. (b) Copper + zinc sulfate. (c) Magnesium + iron oxide. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      '(a) YES — Fe is more reactive than Cu, displaces it. Equation Fe + CuSO₄ → FeSO₄ + Cu',
      '(b) NO — Cu is less reactive than Zn',
      'No displacement, no reaction',
      '(c) YES — Mg is more reactive than Fe',
      'Mg displaces Fe from iron oxide → MgO + Fe (thermite-style)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'chem-6.7.w1', type: 'choice', question: 'Zn + CuSO₄ → ZnSO₄ + ___ .', options: ['SO₂', 'Cu', 'O₂', 'H₂'], answer: 'Cu', hint1: 'Solid copper formed', hint2: 'Pinkish layer', hint3: 'Element symbol' },
      { id: 'chem-6.7.w2', type: 'choice', question: 'Will copper displace zinc?', options: ['Yes', 'No', 'Sometimes', 'Only when hot'], answer: 'No', hint1: 'Cu less reactive', hint2: 'Below Zn in series', hint3: 'No reaction' },
      { id: 'chem-6.7.w3', type: 'text', question: 'A more reactive metal ___ a less reactive one from a compound.', answer: 'displaces', hint1: '9 letters', hint2: 'Pushes out', hint3: 'Starts with d' },
    ],
    level1: [
      { id: 'chem-6.7.1', type: 'choice', question: 'Will Mg displace Cu from CuSO₄?', options: ['Yes', 'No', 'Maybe', 'Only with heat'], answer: 'Yes', hint1: 'Mg more reactive', hint2: 'Above Cu in series', hint3: 'Goes ahead' },
      { id: 'chem-6.7.2', type: 'text', question: 'In Zn + CuSO₄, the blue colour fades because the CuSO₄ becomes ___ ZnSO₄.', answer: 'colourless', hint1: '10 letters', hint2: 'Zn salts are clear', hint3: 'Lacks blue' },
    ],
    level2: [
      { id: 'chem-6.7.3', type: 'choice', question: 'Aluminium + iron oxide → ?', options: ['No reaction', 'Aluminium oxide + iron (thermite)', 'Iron + aluminium', 'Steel'], answer: 'Aluminium oxide + iron (thermite)', hint1: 'Al more reactive than Fe', hint2: 'Used in welding', hint3: 'Thermite reaction' },
      { id: 'chem-6.7.4', type: 'choice', question: 'Will silver displace copper from copper sulfate?', options: ['Yes', 'No', 'Only in heat', 'Maybe'], answer: 'No', hint1: 'Ag less reactive', hint2: 'Below Cu', hint3: 'No reaction' },
    ],
    level3: [
      { id: 'chem-6.7.5', type: 'choice', question: 'A blue copper sulfate solution turns pale green when iron is added. Why?', options: ['Iron sulfate (green) replaces copper sulfate', 'Heating effect', 'Light reflects differently', 'Blue dye fades'], answer: 'Iron sulfate (green) replaces copper sulfate', hint1: 'Displacement', hint2: 'Iron salt is green', hint3: 'Linked to oxidation state' },
      { id: 'chem-6.7.6', type: 'text', question: 'The reaction of aluminium with iron oxide to weld rails is called the ___ reaction.', answer: 'thermite', hint1: '8 letters', hint2: 'Releases lots of heat', hint3: 'Starts with t' },
    ],
  },
};

// =====================================================================
// 6.8  COMBUSTION AND OXIDATION
// =====================================================================
const COMBUSTION_TOPIC = {
  id: 'chem-6.8',
  title: 'Combustion and Oxidation',
  slug: 'combustion-and-oxidation',
  pageRef: 'Section 6 · Topic 8',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Burning = combustion = oxidation',
        body:
          'Combustion is a fast reaction with oxygen that releases energy as heat (and usually light). Slower reactions with oxygen — like rusting — are also called oxidation, even though they don\u2019t look like burning. In all of them, a substance combines with oxygen to make an oxide.',
        check: {
          question: 'Combustion always involves which gas?',
          options: ['Carbon dioxide', 'Oxygen', 'Nitrogen', 'Hydrogen'],
          answer: 'Oxygen',
          explainer: 'No oxygen, no combustion — that\u2019s why blowing on embers (more O₂) makes them flare.',
        },
      },
      {
        heading: 'Complete vs incomplete combustion',
        body:
          'With PLENTY of oxygen, hydrocarbon fuels burn completely: fuel + oxygen → carbon dioxide + water. With LIMITED oxygen, you get incomplete combustion: fuel + oxygen → carbon monoxide (or soot, carbon) + water. Carbon monoxide (CO) is colourless, odourless and toxic — that\u2019s why faulty boilers can kill.',
        check: {
          question: 'Complete combustion of methane gives water and…',
          options: ['Carbon monoxide', 'Carbon dioxide', 'Soot', 'Hydrogen'],
          answer: 'Carbon dioxide',
          explainer: 'CH₄ + 2O₂ → CO₂ + 2H₂O. CO and soot only appear when oxygen is short.',
        },
      },
      {
        heading: 'Other oxidation reactions',
        body:
          'Rusting: iron + oxygen + water → hydrated iron oxide (rust). Slow but constant — costs the UK billions every year. Tarnishing: silver darkens because silver + sulfur compounds in the air → silver sulfide. Respiration in your body is also a slow oxidation: glucose + oxygen → CO₂ + water + energy. Same reaction as combustion of glucose, just slower.',
        check: {
          question: 'Which is also a form of oxidation?',
          options: ['Photosynthesis', 'Rusting', 'Melting', 'Boiling'],
          answer: 'Rusting',
          explainer: 'Iron is gaining oxygen → oxidation, even though no flame.',
        },
      },
    ],
    keyPoints: [
      'Combustion = fast oxidation with oxygen (releases heat)',
      'Complete: fuel + O₂ → CO₂ + H₂O',
      'Incomplete: fuel + O₂ → CO + H₂O (toxic CO!)',
      'Rusting: iron + O₂ + H₂O → rust',
      'Respiration is slow oxidation of glucose',
      'Lots of oxygen = clean burn; little oxygen = dirty burn',
    ],
    workedExamples: [
      {
        problem: 'A gas boiler is starved of oxygen. Predict TWO problems and explain each.',
        steps: [
          { step: 1, instruction: 'Identify issue', working: 'Incomplete combustion' },
          { step: 2, instruction: 'Problem 1', working: 'Carbon monoxide is produced — toxic and undetectable' },
          { step: 3, instruction: 'Problem 2', working: 'Soot/carbon builds up — black deposits, less efficient' },
        ],
        answer: 'CO poisoning risk + soot build-up.',
        commonMistake: 'Saying it just "burns less brightly" — the dangerous bit is the CO production.',
      },
    ],
    whyItWorks:
      'Oxygen is hungry for electrons, so anything that can hand its electrons over (most fuels and metals can) reacts with oxygen given the chance. Speed depends on temperature: a match supplies the heat needed to start combustion; rusting just plods along at room temperature.',
  },
  glossary: [
    { term: 'Combustion', definition: 'A fast reaction with oxygen that releases energy as heat and light.' },
    { term: 'Oxidation', definition: 'A reaction where a substance gains oxygen.' },
    { term: 'Complete combustion', definition: 'Burning with enough oxygen → CO₂ + water.' },
    { term: 'Incomplete combustion', definition: 'Burning with too little oxygen → CO and/or soot + water.' },
    { term: 'Carbon monoxide', definition: 'A colourless, odourless toxic gas (CO) made by incomplete combustion.' },
    { term: 'Rust', definition: 'Hydrated iron oxide — formed when iron oxidises in air with water present.' },
  ],
  examQuestion: {
    prompt:
      'Methane is the main gas in natural gas. (a) Write the word equation for the COMPLETE combustion of methane. (b) Explain why incomplete combustion is dangerous in homes. (c) Suggest TWO ways to prevent rust on a steel garden gate. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Methane + oxygen → carbon dioxide + water',
      'Incomplete combustion makes carbon monoxide',
      'CO is colourless and odourless and toxic — can be deadly',
      'Paint the gate (barrier method)',
      'OR oil it / galvanise it / coat with zinc / use stainless steel (any second method)',
    ],
  },
  practice: {
    warmUp: [
      { id: 'chem-6.8.w1', type: 'text', question: 'Burning needs the gas ___ .', answer: 'oxygen', hint1: '6 letters', hint2: 'O₂', hint3: 'In the air' },
      { id: 'chem-6.8.w2', type: 'choice', question: 'Complete combustion of fuel produces…', options: ['CO + water', 'CO₂ + water', 'CO₂ + nitrogen', 'Hydrogen'], answer: 'CO₂ + water', hint1: 'Plenty of oxygen', hint2: 'Clean burn', hint3: 'CO₂ for "complete"' },
      { id: 'chem-6.8.w3', type: 'choice', question: 'Carbon monoxide is dangerous because…', options: ['It smells bad', 'It is colourless and toxic', 'It is hot', 'It glows'], answer: 'It is colourless and toxic', hint1: 'Hard to detect', hint2: 'Stops blood carrying O₂', hint3: 'Silent killer' },
    ],
    level1: [
      { id: 'chem-6.8.1', type: 'text', question: 'Iron + oxygen + water → ___ (orange flaky solid).', answer: 'rust', hint1: '4 letters', hint2: 'Hydrated iron oxide', hint3: 'Starts with r' },
      { id: 'chem-6.8.2', type: 'choice', question: 'Which is NOT a form of oxidation?', options: ['Burning', 'Rusting', 'Respiration', 'Melting'], answer: 'Melting', hint1: 'Physical change only', hint2: 'No O₂ added', hint3: 'No new substance' },
    ],
    level2: [
      { id: 'chem-6.8.3', type: 'choice', question: 'Why does incomplete combustion happen?', options: ['Too little fuel', 'Too little oxygen', 'Too much pressure', 'Cold weather'], answer: 'Too little oxygen', hint1: 'Limited supply', hint2: 'Restricts CO₂ formation', hint3: 'Makes CO instead' },
      { id: 'chem-6.8.4', type: 'text', question: 'Painting steel stops rust by acting as a ___ (keeping out air and water).', answer: 'barrier', hint1: '7 letters', hint2: 'Protective layer', hint3: 'Starts with b' },
    ],
    level3: [
      { id: 'chem-6.8.5', type: 'choice', question: 'Why is fast oxidation called combustion?', options: ['It only burns metals', 'It is rapid and releases heat and light', 'It is silent', 'It needs water'], answer: 'It is rapid and releases heat and light', hint1: 'Visible reaction', hint2: 'Flame appears', hint3: 'Energy released' },
      { id: 'chem-6.8.6', type: 'choice', question: 'Respiration in cells is most similar to…', options: ['Photosynthesis', 'Slow combustion of glucose', 'Filtration', 'Distillation'], answer: 'Slow combustion of glucose', hint1: 'Glucose + O₂ → CO₂ + H₂O + energy', hint2: 'Same equation as burning', hint3: 'Body temperature though' },
    ],
  },
};

// ---------- assemble the section ----------
const BASE_TOPICS = [
  REACTIONS_TOPIC,
  EQUATIONS_TOPIC,
  CONSERVATION_TOPIC,
  ACIDS_TOPIC,
  NEUTRALISATION_TOPIC,
  REACTIVITY_TOPIC,
  DISPLACEMENT_TOPIC,
  COMBUSTION_TOPIC,
];

// authoredTopics.json overrides any topic by id (for the PDF pipeline).
export const chemicalChangesTopics = BASE_TOPICS.map((t) =>
  AUTHORED_BY_ID[t.id] ? AUTHORED_BY_ID[t.id] : t,
);
